import { useEffect, useState } from "react";
import agent from "../utils/axios";

/**
 * AB#6562: how a job describes itself to the page. The page renders one card per job from
 * this alone — whether it writes a report, what a unit of work is called, what the apply
 * dialog warns about — so a new job needs no client change beyond the job list.
 */
export interface MaintenanceJobInfo {
  name: string;
  title: string;
  hasReport: boolean;
  unitNoun: string | null;
  applyConfirmation: string | null;
}

/** AB#6563: what GET /api/Maintenance/access returns. */
export interface MaintenanceAccess {
  canOperate: boolean;
  user: string | null;
  jobs: MaintenanceJobInfo[];
}

const denied: MaintenanceAccess = { canOperate: false, user: null, jobs: [] };

let cached: MaintenanceAccess | null = null;
let inflight: Promise<MaintenanceAccess> | null = null;

const load = (): Promise<MaintenanceAccess> => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = agent.Maintenance.access()
      .then((a: MaintenanceAccess) => {
        cached = a;
        return a;
      })
      .catch(() => {
        // Unauthenticated or API down: behave as "not an operator"; do not cache so a
        // later sign-in can succeed.
        inflight = null;
        return denied;
      });
  }
  return inflight;
};

/**
 * Whether the signed-in user may see and use the Maintenance page. The answer comes from
 * the API's operator allow-list, not from a token role, so it is fetched once and shared.
 */
export const useMaintenanceAccess = () => {
  const [access, setAccess] = useState<MaintenanceAccess>(cached ?? denied);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let alive = true;
    load().then((a) => {
      if (alive) {
        setAccess(a);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  return { ...access, loading };
};
